/**
 * DocumentViewerModal
 * Opens a document stored in Cloudflare R2 via a temporary Signed URL.
 * Certificado digital: pede senha antes de gerar a URL.
 */

import { useEffect, useState, useCallback } from 'react';
import { X, Download, Loader2, AlertTriangle, RefreshCw, FileText, Image, Lock } from 'lucide-react';
import type { DoctorDocumentDTO, DocumentViewUrlDTO } from '@/services/doctors';

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DoctorDocumentDTO | null;
  userId: string;
  /** Function to fetch the signed URL — allows both doctor and admin contexts */
  fetchViewUrl: (
    userId: string,
    documentId: string,
    options?: { password?: string },
  ) => Promise<{
    success: boolean;
    data?: DocumentViewUrlDTO;
    error?: string;
  }>;
}

export function DocumentViewerModal({
  isOpen,
  onClose,
  document,
  userId,
  fetchViewUrl,
}: DocumentViewerModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewData, setViewData] = useState<DocumentViewUrlDTO | null>(null);
  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);

  const needsPassword = Boolean(
    document?.requiresPasswordToUnlock ||
      document?.documentType === 'DIGITAL_CERTIFICATE' ||
      document?.type === 'DIGITAL_CERTIFICATE',
  );

  const loadSignedUrl = useCallback(
    async (pwd?: string) => {
      if (!document) return;

      if (!document.hasFile) {
        setError(
          'Este documento foi criado antes da migração para Cloudflare R2. ' +
            'Por favor, faça um novo upload.',
        );
        return;
      }

      if (needsPassword && !pwd && !unlocked) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setViewData(null);

      try {
        const response = await fetchViewUrl(userId, document.id, {
          password: pwd || password || undefined,
        });
        if (response.success && response.data) {
          setViewData(response.data);
          setUnlocked(true);
        } else {
          setError(response.error || 'Erro ao obter URL do documento');
        }
      } catch (err) {
        setError('Erro de conexão ao obter documento');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [document, userId, fetchViewUrl, needsPassword, unlocked, password],
  );

  useEffect(() => {
    if (isOpen && document) {
      setPassword('');
      setUnlocked(false);
      setViewData(null);
      setError(null);
      if (!needsPassword) {
        void loadSignedUrl();
      }
    } else {
      setViewData(null);
      setError(null);
      setPassword('');
      setUnlocked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open/document change
  }, [isOpen, document?.id, needsPassword]);

  if (!isOpen) return null;

  const isPdf =
    viewData?.mimeType === 'application/pdf' ||
    document?.originalFileName?.toLowerCase().endsWith('.pdf');
  const isImage =
    viewData?.mimeType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(document?.originalFileName ?? '');
  const isVideo =
    viewData?.mimeType?.startsWith('video/') ||
    /\.(mp4|webm|mov)$/i.test(document?.originalFileName ?? '');

  const docTypeLabel: Record<string, string> = {
    CRM: 'CRM',
    SPECIALTY_CERTIFICATE: 'Certificado de Especialidade',
    INSCRIPTION_PROOF: 'Comprovante de Inscrição',
    CONFIRMATION_VIDEO: 'Vídeo de confirmação',
    DIGITAL_CERTIFICATE: 'Certificado digital',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              {needsPassword ? (
                <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {document
                  ? (docTypeLabel[document.documentType || ''] ?? document.documentType)
                  : 'Documento'}
              </h2>
              {document && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {document.originalFileName}
                  {document.certificateKind ? ` • ${document.certificateKind}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {viewData && (
              <a
                href={viewData.signedUrl}
                download={viewData.fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-neutral-950 relative min-h-[400px]">
          {needsPassword && !unlocked && !viewData && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
              <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-full">
                <Lock className="w-8 h-8 text-teal-600 dark:text-teal-400" />
              </div>
              <div className="text-center max-w-sm w-full space-y-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Senha do certificado
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Informe a senha do certificado digital para visualizar ou baixar o arquivo.
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha do certificado"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                  autoComplete="off"
                />
                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                <button
                  onClick={() => void loadSignedUrl(password)}
                  disabled={!password || isLoading}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                  Desbloquear
                </button>
              </div>
            </div>
          )}

          {isLoading && (unlocked || !needsPassword) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Carregando documento...</p>
            </div>
          )}

          {error && !isLoading && (!needsPassword || unlocked) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-full">
                <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-center max-w-md">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Documento indisponível
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
                <button
                  onClick={() => void loadSignedUrl(password)}
                  className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-neutral-800 rounded-lg"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {viewData && !isLoading && (
            <>
              {isPdf && (
                <iframe
                  src={`${viewData.signedUrl}#toolbar=1&navpanes=1`}
                  className="w-full h-full min-h-[600px]"
                  title={viewData.fileName}
                  style={{ border: 'none' }}
                />
              )}
              {isImage && !isPdf && !isVideo && (
                <div className="flex items-center justify-center p-4 min-h-[400px]">
                  <img
                    src={viewData.signedUrl}
                    alt={viewData.fileName}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-md"
                  />
                </div>
              )}
              {isVideo && !isPdf && (
                <div className="flex items-center justify-center p-4 min-h-[400px]">
                  <video
                    src={viewData.signedUrl}
                    controls
                    playsInline
                    className="max-w-full max-h-[70vh] rounded-lg shadow-md bg-black"
                  >
                    Seu navegador não suporte a reprodução deste vídeo.
                  </video>
                </div>
              )}
              {!isPdf && !isImage && !isVideo && (
                <div className="flex flex-col items-center justify-center gap-4 p-8 min-h-[400px]">
                  <FileText className="w-12 h-12 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400 text-center">
                    Visualização não suportada para este tipo de arquivo.
                    <br />
                    Use o botão Download para abrir o arquivo.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {viewData && (
          <div className="px-6 py-3 bg-gray-50 dark:bg-neutral-900 border-t border-gray-200 dark:border-neutral-800 flex-shrink-0">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              URL válida até {new Date(viewData.expiresAt).toLocaleTimeString('pt-BR')} • Acesso
              seguro via Cloudflare R2
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
